import {
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Box,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { useState } from 'react';

export function stableSort(array, comparator) {
  const stabilizedThis = (array || []).map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  if (typeof b[orderBy] === 'string' && typeof a[orderBy] === 'string') {
    return b[orderBy].toLowerCase() < a[orderBy].toLowerCase() ? -1 : 1;
  }

  return b[orderBy] < a[orderBy] ? -1 : 1;
}

export function getComparator(order, orderBy, customComparators = {}) {
  if (customComparators[orderBy]) {
    return (a, b) => customComparators[orderBy](a, b, order);
  }

  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#f8fafb',
    color: '#4b5563',
    fontWeight: '700',
    whiteSpace: 'nowrap',
    overflow: `hidden !important`,
    textOverflow: 'ellipsis',
  },
  root: {
    padding: '8px',
    lineHeight: '1.5',
  },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: 'rgba(183, 244, 216, 0.7)',
    },
  },
}))(TableRow);

function JFTableHead({
  orderDirection,
  orderedBy,
  onHeaderCellClick,
  columns,
  hasCollapsedContent,
}) {
  return (
    <TableHead>
      <TableRow>
        {hasCollapsedContent && <StyledTableCell />}
        {columns.map((column) => (
          <StyledTableCell
            key={column.id}
            align={column.align}
          >
            {column.disableSort ? (
              column.label
            ) : (
              <TableSortLabel
                active={column.id === orderedBy}
                direction={orderedBy === column.id ? orderDirection : 'asc'}
                onClick={() => onHeaderCellClick(column.id)}
              >
                {column.label}
              </TableSortLabel>
            )}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function JFTableRow({
  columns,
  data,
  collapsedContent,
  onRowSelection,
  selectedRowIds,
  hasCollapsedContent,
  onCollapseToggle = () => {},
}) {
  const [open, setOpen] = useState(false);

  if (!collapsedContent) {
    return (
      <StyledTableRow
        onClick={() => {
          onRowSelection && onRowSelection(data);
        }}
        selected={selectedRowIds && selectedRowIds.has(data.id)}
      >
        {columns.map((column) => (
          <StyledTableCell
            key={column.id}
            align={column.align}
            style={{ width: column.width, maxWidth: column.width }}
          >
            {column.cell ? column.cell(data) : data[column.id]}
          </StyledTableCell>
        ))}
      </StyledTableRow>
    );
  }

  return (
    <>
      <StyledTableRow>
        <StyledTableCell style={{ width: 50, borderBottom: 'none' }}>
          {hasCollapsedContent(data) && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => {
                setOpen(!open);
                onCollapseToggle(data);
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </StyledTableCell>
        {columns.map((column) => (
          <StyledTableCell
            key={column.id}
            align={column.align}
            style={{ borderBottom: 'none', width: column.width, minWidth: column.width }}
          >
            {column.cell ? column.cell(data) : data[column.id]}
          </StyledTableCell>
        ))}
      </StyledTableRow>
      <TableRow>
        <StyledTableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            height: 'auto',
          }}
          colSpan={columns.length + 1}
        >
          <Collapse
            in={open}
            timeout="auto"
            unmountOnExit
          >
            <Box sx={{ margin: 1 }}>{collapsedContent(data)}</Box>
          </Collapse>
        </StyledTableCell>
      </TableRow>
    </>
  );
}

/**
 * JFTable component to render a table with sorting functionality.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {Array<T extends { id: string}>} props.columns - The columns configuration for the table.
 * @param {string} props.orderedBy - The column id by which the data is ordered.
 * @param {'asc' | 'desc'} props.orderDirection - The direction of the ordering.
 * @param {Function} props.onHeaderCellClick - The function to call when a header cell is clicked.
 * @param {boolean} [props.showFooter=false] - Whether to show the footer with pagination.
 * @param {number} [props.rowHeight=35] - The height of each row.
 * @param {string} [props.rowIdProp='id'] - The property to use as the row key.
 * @param {Function} props.collapsedContent - The function to render the content of the collapsed row.
 * @returns {JSX.Element} The JFTable component.
 */
export default function JFTable(props) {
  const {
    data,
    columns,
    orderedBy,
    orderDirection,
    onHeaderCellClick = () => {},
    rowHeight = 35,
    rowIdProp = 'id',
    collapsedContent,
    hasCollapsedContent,
    selectedRowIds = new Set(),
    onRowSelection,
    hideHeader,
    headerTop = 0,
    onCollapseToggle,
  } = props;

  return (
    <Table
      stickyHeader
      sx={{ '& .MuiTableCell-stickyHeader': { top: headerTop } }}
    >
      {!hideHeader && (
        <JFTableHead
          columns={columns}
          orderedBy={orderedBy}
          orderDirection={orderDirection}
          onHeaderCellClick={onHeaderCellClick}
          hasCollapsedContent={!!collapsedContent}
        />
      )}
      <TableBody>
        {data.map((datum) => {
          return (
            <JFTableRow
              height={rowHeight}
              key={datum[rowIdProp]}
              columns={columns}
              data={datum}
              collapsedContent={collapsedContent}
              hasCollapsedContent={hasCollapsedContent}
              onRowSelection={onRowSelection}
              selectedRowIds={selectedRowIds}
              onCollapseToggle={onCollapseToggle}
            />
          );
        })}
      </TableBody>
    </Table>
  );
}

JFTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      cell: PropTypes.func,
    }),
  ),
  orderedBy: PropTypes.string,
  orderDirection: PropTypes.oneOf(['asc', 'desc']),
  onHeaderCellClick: PropTypes.func,
  showFooter: PropTypes.bool,
  rowHeight: PropTypes.number,
};
